import { type ReactElement } from 'react';
import { BriefcaseBusiness } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@/auth/usePermissions';
import { PageHeader } from '@/components/shell/PageHeader';
import { JobsListPage } from '@/features/platform/components/JobsListPage';

/**
 * Platform console — Jobs tab (PTV2-1021 Phase 5).
 *
 * Renders global/cluster-orchestration jobs. The backend routes the
 * `GET /api/v1/jobs` response by user context: a global-admin sees global jobs,
 * a tenant user sees their own. This page is only reachable via `openPlatform`
 * (configured in routes/index.tsx), so the caller is always a global-plane user.
 *
 * The Restart action is gated on `managePlatform` (PLATFORM_ADMIN only).
 */
export function PlatformJobsPage(): ReactElement {
  const { t } = useTranslation();
  const { canManagePlatform } = usePermissions();

  return (
    <>
      <PageHeader
        icon={<BriefcaseBusiness className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.platform.jobs.heading')}
        subtitle={t('routes.platform.jobs.subheading')}
      />
      <JobsListPage
        caption={t('routes.platform.jobs.caption')}
        captionNote={t('routes.platform.jobs.captionNote')}
        i18nPrefix="routes.platform.jobs"
        canRestart={canManagePlatform}
      />
    </>
  );
}
